import React, { useEffect, useState } from 'react';
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Code2,
  FileText,
  Globe,
  LifeBuoy,
  Lock,
  Mail,
  MapPin,
  MessageCircle,
  MessagesSquare,
  MousePointer2,
  Pause,
  Phone,
  Play,
  RefreshCw,
  Rocket,
  Search,
  ShieldCheck,
  Star,
  X,
} from 'lucide-react';
import { IMG, SHARED_IMG } from './shared';
import './WebsiteGuestSplit.css';

/* ==========================================================================
   Guest experience row — overlapping avatars, nudging arrow, device circles
   ========================================================================== */

// The guest and device photos already ship with the /turo-alternative port as
// .webp; reuse them instead of duplicating the v0 PNGs.
const GUESTS = [
  { src: `${SHARED_IMG}/guests/leisure.webp`, label: 'Leisure', alt: 'Leisure rental customer' },
  { src: `${SHARED_IMG}/guests/rideshare.webp`, label: 'Rideshare', alt: 'Rideshare driver' },
  { src: `${SHARED_IMG}/guests/corporate.webp`, label: 'Corporate', alt: 'Corporate rental customer' },
  { src: `${SHARED_IMG}/guests/delivery.webp`, label: 'Delivery', alt: 'Food delivery courier' },
  { src: `${SHARED_IMG}/guests/packages.webp`, label: 'Packages', alt: 'Package delivery driver' },
];

const DEVICES = [
  { src: `${SHARED_IMG}/devices/desktop.webp`, label: 'Desktop', size: 28 },
  { src: `${SHARED_IMG}/devices/tablet.webp`, label: 'Tablet', size: 24 },
  { src: `${SHARED_IMG}/devices/mobile.webp`, label: 'Mobile', size: 20 },
];

const GuestExperienceRow = () => (
  <div className="ilp-wg-exp">
    {/* Guest avatars — overlapping circles */}
    <div className="ilp-wg-exp-group">
      {GUESTS.map((guest, i) => (
        <div
          key={guest.label}
          className="ilp-wg-exp-item"
          style={{ marginLeft: i === 0 ? 0 : '-14px', zIndex: GUESTS.length - i }}
        >
          <div className="ilp-wg-exp-circle">
            <img src={guest.src} alt={guest.alt} width={48} height={48} className="ilp-wg-exp-photo" />
          </div>
          <span className="ilp-wg-exp-label">{guest.label}</span>
        </div>
      ))}
    </div>

    {/* Arrow — nudges toward the devices every 3s */}
    <ArrowRight className="ilp-arrow-nudge ilp-wg-exp-arrow" strokeWidth={2.5} aria-hidden="true" />

    {/* Device circles, sized to match the guest avatars */}
    <div className="ilp-wg-exp-group">
      {DEVICES.map((device, i) => (
        <div
          key={device.label}
          className="ilp-wg-exp-item"
          style={{ marginLeft: i === 0 ? 0 : '-14px', zIndex: DEVICES.length - i }}
        >
          <div className="ilp-wg-exp-circle ilp-wg-exp-circle--device">
            <img
              src={device.src}
              alt={`${device.label} device`}
              className="ilp-wg-exp-device"
              style={{ width: device.size, height: device.size }}
            />
          </div>
          <span className="ilp-wg-exp-label">{device.label}</span>
        </div>
      ))}
    </div>
  </div>
);

/* ==========================================================================
   Collapsed teaser mockups — decorative static UI
   ========================================================================== */

const MOCK_VEHICLES = [
  { name: 'Toyota RAV4', type: 'SUV · Automatic', price: '$68', src: `${IMG}/inshur/mockup/vehicle-suv.png` },
  { name: 'Honda Accord', type: 'Sedan · Automatic', price: '$54', src: `${IMG}/inshur/mockup/honda-accord.png` },
];

/** Static mockup of a branded rental website, inside the collapsed teaser. */
const RentalWebsiteMockup = () => (
  <div aria-hidden="true" className="ilp-wg-mock">
    <div className="ilp-wg-mock-chrome">
      <span className="ilp-wg-mock-dot" />
      <span className="ilp-wg-mock-dot" />
      <span className="ilp-wg-mock-dot" />
      <div className="ilp-wg-mock-url">driveluxe.com</div>
    </div>

    <div className="ilp-wg-mock-hero">
      <img
        src={`${IMG}/inshur/mockup/rental-hero.png`}
        alt=""
        className="ilp-wg-fill"
      />
      <div className="ilp-wg-mock-hero-scrim" />
      <p className="ilp-wg-mock-brand">DriveLuxe Rentals</p>
      <p className="ilp-wg-mock-tagline">Find your perfect ride</p>
    </div>

    <div className="ilp-wg-mock-search">
      <div className="ilp-wg-mock-field">
        <MapPin size={14} />
        <span>Los Angeles</span>
      </div>
      <div className="ilp-wg-mock-field ilp-wg-mock-field--dates">
        <CalendarDays size={14} />
        <span>Aug 4 – 9</span>
      </div>
      <span className="ilp-wg-mock-searchbtn">
        <Search size={16} />
      </span>
    </div>

    <div className="ilp-wg-mock-grid">
      {MOCK_VEHICLES.map((v) => (
        <div key={v.name} className="ilp-wg-mock-card">
          <div className="ilp-wg-mock-card-img">
            <img src={v.src} alt="" className="ilp-wg-fill" />
          </div>
          <div className="ilp-wg-mock-card-body">
            <p className="ilp-wg-mock-card-name">{v.name}</p>
            <p className="ilp-wg-mock-card-type">{v.type}</p>
            <div className="ilp-wg-mock-card-foot">
              <p className="ilp-wg-mock-card-price">
                {v.price}
                <span>/day</span>
              </p>
              <span className="ilp-wg-mock-book">Book</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MOCK_ACTIONS = [
  { Icon: LifeBuoy, label: 'Roadside assistance' },
  { Icon: FileText, label: 'Rental agreement' },
  { Icon: ShieldCheck, label: 'Insurance card' },
  { Icon: MessageCircle, label: 'Contact host' },
];

/** Static mockup of a guest trip-management page, inside the collapsed teaser. */
const GuestAccountMockup = () => (
  <div aria-hidden="true" className="ilp-wg-mock">
    <div className="ilp-wg-mock-chrome">
      <span className="ilp-wg-mock-dot" />
      <span className="ilp-wg-mock-dot" />
      <span className="ilp-wg-mock-dot" />
      <div className="ilp-wg-mock-url">account.driveluxe.com/trips</div>
    </div>

    <div className="ilp-wg-acct">
      <div className="ilp-wg-acct-head">
        <p>Welcome, Michael</p>
        <span className="ilp-wg-acct-badge">
          <span className="ilp-wg-acct-badge-dot" />
          On Trip
        </span>
      </div>

      <div className="ilp-wg-acct-trip">
        <div className="ilp-wg-acct-trip-top">
          <div className="ilp-wg-acct-trip-img">
            <img src={`${IMG}/inshur/mockup/honda-accord.png`} alt="" className="ilp-wg-fill" />
          </div>
          <div className="ilp-wg-acct-trip-meta">
            <p className="ilp-wg-acct-trip-name">Honda Accord</p>
            <p className="ilp-wg-acct-trip-type">Sedan · Automatic</p>
            <p className="ilp-wg-acct-trip-loc">
              <MapPin size={12} />
              LAX International Airport
            </p>
          </div>
        </div>

        <div className="ilp-wg-acct-modify">
          <p className="ilp-wg-acct-modify-label">Modify trip</p>
          <div className="ilp-wg-acct-modify-row">
            <div className="ilp-wg-acct-chip">
              <CalendarDays size={12} />
              <span>Aug 4 – 9</span>
            </div>
            <div className="ilp-wg-acct-chip">
              <Clock size={12} />
              <span>10:00 AM</span>
            </div>
            <span className="ilp-wg-acct-modify-btn">Modify</span>
          </div>
        </div>
      </div>

      <div className="ilp-wg-acct-actions">
        {MOCK_ACTIONS.map(({ Icon, label }) => (
          <div key={label} className="ilp-wg-acct-action">
            <span className="ilp-wg-acct-action-icon">
              <Icon size={16} />
            </span>
            <span className="ilp-wg-acct-action-label">{label}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ==========================================================================
   Expanded "Rental Website" panel — brand technology
   ========================================================================== */

/* Mock inventory rendered on the published site */
const ALL_CARS = [
  { name: 'Tesla Model 3', type: 'Electric', price: '$89', img: `${IMG}/renter/ev.png`, rating: '4.9' },
  { name: 'Jeep Wrangler', type: 'SUV', price: '$75', img: `${IMG}/renter/suv.png`, rating: '4.8' },
  { name: 'BMW 4 Series', type: 'Convertible', price: '$120', img: `${IMG}/renter/convertible.png`, rating: '5.0' },
  { name: 'Rivian R1S', type: 'Electric SUV', price: '$140', img: `${IMG}/renter/suv.png`, rating: '4.9' },
  { name: 'Ford Mustang', type: 'Convertible', price: '$110', img: `${IMG}/renter/convertible.png`, rating: '4.7' },
  { name: 'Tesla Model Y', type: 'Electric', price: '$99', img: `${IMG}/renter/ev.png`, rating: '4.8' },
];

const REVIEWS = [
  { name: 'Marcus T.', text: 'Booked in two minutes. The car was spotless.' },
  { name: 'Priya S.', text: "Best rental experience I've had in Miami." },
];

/* translateY of the live page per scroll step (hero → vehicles → reviews → contact) */
const SCROLL_OFFSETS = [0, -185, -300, -369];

/* Embed flow timeline: 0 empty field · 1 snippet pasted · 2 Embed link pressed ·
   3 live hero · 4 vehicles · 5 reviews · 6 contact */
const EMBED_DURATIONS = [1600, 1700, 1200, 1600, 1500, 1500, 1900];

const SiteHeader = () => (
  <div className="ilp-wg-site-header">
    <div className="ilp-wg-site-brand">
      <span className="ilp-wg-site-logo">C</span>
      <span className="ilp-wg-site-name">Coastline Cars</span>
    </div>
    <div className="ilp-wg-site-nav">
      <span>Fleet</span>
      <span>Deals</span>
      <span className="ilp-wg-site-cta">Book now</span>
    </div>
  </div>
);

/** Published live site with a sticky header and a scroll-through body. */
const LiveSiteScroll = ({ scrollStep }) => (
  <div className="ilp-wg-live ilp-fade-slide">
    <div className="ilp-wg-live-sticky">
      <SiteHeader />
    </div>

    <div
      className="ilp-wg-live-scroll"
      style={{ transform: `translateY(${SCROLL_OFFSETS[scrollStep]}px)` }}
    >
      {/* Lifestyle hero */}
      <div className="ilp-wg-live-hero">
        <img src={`${IMG}/renter/leisure-lifestyle.png`} alt="" className="ilp-wg-fill" />
        <div className="ilp-wg-live-hero-scrim" />
        <div className="ilp-wg-live-hero-copy">
          <p className="ilp-wg-live-hero-title">Your Miami adventure starts here.</p>
          <p className="ilp-wg-live-hero-sub">Trusted local cars, booked in minutes.</p>
          <div className="ilp-wg-live-bookbar">
            <div className="ilp-wg-live-bookfield">
              <MapPin size={12} />
              <span>Miami, FL</span>
            </div>
            <div className="ilp-wg-live-bookfield ilp-wg-live-bookfield--split">
              <CalendarDays size={12} />
              <span>Jul 4 – Jul 8</span>
            </div>
            <span className="ilp-wg-live-booksearch">
              <Search size={10} /> Search
            </span>
          </div>
        </div>
      </div>

      {/* Vehicles */}
      <div className="ilp-wg-live-block">
        <p className="ilp-wg-live-h">Available now in Miami</p>
        <div className="ilp-wg-live-cars">
          {ALL_CARS.map((car, i) => (
            <div key={`${car.name}-${i}`} className="ilp-wg-live-car">
              <div className="ilp-wg-live-car-img">
                <img src={car.img} alt="" className="ilp-wg-fill" />
              </div>
              <div className="ilp-wg-live-car-body">
                <div className="ilp-wg-live-car-top">
                  <span className="ilp-wg-live-car-name">{car.name}</span>
                  <span className="ilp-wg-live-car-rating">
                    <Star size={8} className="ilp-wg-live-car-star" />
                    {car.rating}
                  </span>
                </div>
                <p className="ilp-wg-live-car-type">{car.type}</p>
                <div className="ilp-wg-live-car-foot">
                  <span className="ilp-wg-live-car-price">
                    {car.price}
                    <span>/day</span>
                  </span>
                  <span className="ilp-wg-live-car-book">Book</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reviews */}
      <div className="ilp-wg-live-block ilp-wg-live-block--tint">
        <p className="ilp-wg-live-h">What renters say</p>
        <div className="ilp-wg-live-reviews">
          {REVIEWS.map((r) => (
            <div key={r.name} className="ilp-wg-live-review">
              <div className="ilp-wg-live-stars">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={10} className="ilp-wg-live-star" />
                ))}
              </div>
              <p className="ilp-wg-live-review-text">&ldquo;{r.text}&rdquo;</p>
              <p className="ilp-wg-live-review-name">{r.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="ilp-wg-live-block ilp-wg-live-block--tight">
        <p className="ilp-wg-live-h">Get in touch</p>
        <p className="ilp-wg-live-sub">Questions about a booking? Our Miami team is here to help.</p>
        <div className="ilp-wg-live-contacts">
          <div className="ilp-wg-live-contact">
            <Phone size={14} className="ilp-wg-live-contact-icon" />
            <span>(305) 555-0142</span>
          </div>
          <div className="ilp-wg-live-contact">
            <Mail size={14} className="ilp-wg-live-contact-icon" />
            <span>hello@coastline.co</span>
          </div>
        </div>
        <div className="ilp-wg-live-contact ilp-wg-live-contact--wide">
          <MapPin size={14} className="ilp-wg-live-contact-icon" />
          <span>120 Ocean Dr, Miami Beach, FL</span>
        </div>
      </div>

      {/* Footer */}
      <div className="ilp-wg-live-footer">
        <div className="ilp-wg-live-footer-brand">
          <span className="ilp-wg-live-footer-logo">C</span>
          <span className="ilp-wg-live-footer-name">Coastline Cars</span>
        </div>
        <span className="ilp-wg-live-footer-meta">© 2026 · Miami, FL</span>
      </div>
    </div>
  </div>
);

/**
 * Embed-to-live-site flow: paste the snippet into a site's admin, press the
 * embed link, then cut to the published site scrolling.
 */
const EmbedBuilder = () => {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setPhase((p) => (p + 1) % EMBED_DURATIONS.length), EMBED_DURATIONS[phase]);
    return () => clearTimeout(id);
  }, [phase]);

  const selected = phase >= 1;
  const goLivePressed = phase === 2;
  const editing = phase <= 2;
  const live = phase >= 3;
  const scrollStep = Math.min(Math.max(phase - 3, 0), SCROLL_OFFSETS.length - 1);
  const browserUrl = live ? 'coastline.car.rentals.com' : 'app.coastline.com/embed';

  // Cursor targets: 0 hover code start · 1 end of selection drag · 2 on the button
  let cursorStyle = { left: '80%', top: '420px' };
  if (phase === 0) cursorStyle = { left: '20%', top: '150px' };
  else if (phase === 1) cursorStyle = { left: '68%', top: '170px' };

  const selectionClass = `ilp-wg-code-sel${selected ? ' is-selected' : ''}`;

  return (
    <div className="ilp-wg-embed">
      {/* Browser chrome */}
      <div className="ilp-wg-embed-chrome">
        <div className="ilp-wg-embed-dots">
          <span />
          <span />
          <span />
        </div>
        <div className="ilp-wg-embed-url">
          {live ? <Lock size={10} className="ilp-wg-embed-lock" /> : null}
          <span>{browserUrl}</span>
        </div>
        <span className="ilp-wg-embed-avatar" />
      </div>

      {/* Body viewport */}
      <div className="ilp-wg-embed-body">
        {/* Embed admin (phases 0-2) */}
        <div className="ilp-wg-admin" style={{ opacity: live ? 0 : 1 }} aria-hidden={live}>
          <div className="ilp-wg-admin-head">
            <span className="ilp-wg-admin-icon">
              <Code2 size={16} aria-hidden="true" />
            </span>
            <div>
              <p className="ilp-wg-admin-title">Booking widget</p>
              <p className="ilp-wg-admin-sub">Add RentDirect to your existing site</p>
            </div>
          </div>

          <p className="ilp-wg-admin-label">Your embed snippet</p>

          {/* Dark code editor — the snippet highlights as the cursor drags across it */}
          <div className="ilp-wg-code">
            <div className="ilp-wg-code-dots">
              <span />
              <span />
              <span />
            </div>
            <div className="ilp-wg-code-lines">
              <div className="ilp-wg-code-dim">{'// Add the booking widget to any page'}</div>
              <div>
                <span className={selectionClass}>
                  <span className="ilp-wg-code-dim">{'<'}</span>
                  <span className="ilp-wg-code-tag">script</span>{' '}
                  <span className="ilp-wg-code-attr">src</span>
                  <span className="ilp-wg-code-dim">=</span>
                  <span className="ilp-wg-code-val">{'"https://embed.rentdirect.co/booking.js"'}</span>
                </span>
              </div>
              <div className="ilp-wg-code-indent">
                <span className={selectionClass}>
                  <span className="ilp-wg-code-attr">data-org</span>
                  <span className="ilp-wg-code-dim">=</span>
                  <span className="ilp-wg-code-val">{'"coastline-cars"'}</span>{' '}
                  <span className="ilp-wg-code-attr">data-theme</span>
                  <span className="ilp-wg-code-dim">=</span>
                  <span className="ilp-wg-code-val">{'"brand"'}</span>
                </span>
              </div>
              <div className="ilp-wg-code-indent">
                <span className={selectionClass}>
                  <span className="ilp-wg-code-attr">data-location</span>
                  <span className="ilp-wg-code-dim">=</span>
                  <span className="ilp-wg-code-val">{'"miami-fl"'}</span>{' '}
                  <span className="ilp-wg-code-attr">defer</span>
                  <span className="ilp-wg-code-dim">{'></'}</span>
                  <span className="ilp-wg-code-tag">script</span>
                  <span className="ilp-wg-code-dim">{'>'}</span>
                </span>
              </div>
              <div className="ilp-wg-code-dim ilp-wg-code-gap">{'<!-- Booking form renders inline -->'}</div>
              <div>
                <span className="ilp-wg-code-dim">{'<'}</span>
                <span className="ilp-wg-code-tag">div</span>{' '}
                <span className="ilp-wg-code-attr">id</span>
                <span className="ilp-wg-code-dim">=</span>
                <span className="ilp-wg-code-val">{'"rentdirect-booking"'}</span>
                <span className="ilp-wg-code-dim">{'></'}</span>
                <span className="ilp-wg-code-tag">div</span>
                <span className="ilp-wg-code-dim">{'>'}</span>
              </div>
            </div>
          </div>

          {/* Status + embed link button */}
          <div className="ilp-wg-admin-foot">
            <div className="ilp-wg-admin-status">
              <CheckCircle2 size={14} className="ilp-wg-admin-check" aria-hidden="true" />
              {selected ? 'Snippet selected' : 'Embed code ready'}
            </div>
            <span className={`ilp-wg-admin-btn${goLivePressed ? ' is-pressed' : ''}`}>
              <Rocket size={14} aria-hidden="true" /> Embed link
              {goLivePressed && <span key={`golive-${phase}`} className="ilp-wg-ring" />}
            </span>
          </div>
        </div>

        {/* Live site (phases 3-6) */}
        {live && <LiveSiteScroll scrollStep={scrollStep} />}
      </div>

      {/* Animated cursor, editing phases only */}
      {editing && (
        <span className="ilp-wg-cursor" style={cursorStyle}>
          <span className="ilp-wg-cursor-inner">
            <MousePointer2 size={20} className={phase <= 1 ? 'ilp-wg-cursor-light' : 'ilp-wg-cursor-dark'} />
            {goLivePressed && <span key={`ecursor-${phase}`} className="ilp-wg-ring ilp-wg-ring--round" />}
          </span>
        </span>
      )}
    </div>
  );
};

const LAUNCH_OPTIONS = [
  {
    id: 'website',
    Icon: Globe,
    label: 'Full website',
    eyebrow: 'Rental website included',
    title: 'Custom branded, full website, built fast and free',
    copy: 'Launch a custom-branded, white-label rental website in minutes, complete with your logo, colors, and domain — no developer needed.',
    bullets: [
      { title: 'Spotlight', copy: 'Feature your best vehicles front and center.' },
      { title: 'Promotions', copy: 'Run limited-time deals and seasonal offers.' },
      { title: 'Branding', copy: 'Your logo, colors, and voice on every page.' },
      { title: 'Domain', copy: 'Connect your own custom domain in a few clicks.' },
    ],
  },
  {
    id: 'embed',
    Icon: Code2,
    label: 'Embed',
    eyebrow: 'Embed booking engine',
    title: 'Add search and booking on your existing site.',
    copy: 'Paste the rental embed link on your website and get access to the most modern built booking flow.',
    bullets: [
      { title: 'One embed link', copy: 'Copy a single snippet into any page.' },
      { title: 'Works anywhere', copy: 'Add to website platforms or webpages.' },
      { title: 'Always in sync', copy: 'Fleet and pricing update automatically.' },
    ],
  },
];

/** Expanded "Rental Website" panel: copy, mode toggle, and the embed animation. */
const BrandTechnologyContent = () => {
  const [mode, setMode] = useState('website');
  const active = LAUNCH_OPTIONS.find((o) => o.id === mode) || LAUNCH_OPTIONS[0];

  return (
    <div className="ilp-wg-bt">
      <div className="ilp-wg-bt-copy">
        <p className="ilp-wg-bt-eyebrow">{active.eyebrow}</p>
        <h2 className="ilp-wg-bt-title">{active.title}</h2>
        <p className="ilp-wg-bt-sub">{active.copy}</p>

        {/* Feature bullets change with the selected option */}
        <ul className="ilp-wg-bt-bullets">
          {active.bullets.map((b) => (
            <li key={b.title}>
              <Check size={20} className="ilp-wg-bt-check" aria-hidden="true" />
              <span>
                <span className="ilp-wg-bt-bullet-title">{b.title}.</span>{' '}
                <span className="ilp-wg-bt-bullet-copy">{b.copy}</span>
              </span>
            </li>
          ))}
          <li>
            <Check size={20} className="ilp-wg-bt-check" aria-hidden="true" />
            <span>
              <span className="ilp-wg-bt-bullet-title">Responsive.</span>{' '}
              <span className="ilp-wg-bt-bullet-copy">Looks flawless on desktop, tablet, and mobile.</span>
            </span>
          </li>
        </ul>

        {/* Launch mode toggle — sits under the bullets */}
        <div className="ilp-wg-bt-toggle-wrap">
          <div role="tablist" aria-label="Choose how to launch" className="ilp-wg-toggle">
            {LAUNCH_OPTIONS.map((opt) => {
              const isActive = mode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setMode(opt.id)}
                  className={`ilp-wg-toggle-btn${isActive ? ' is-active' : ''}`}
                >
                  <opt.Icon size={16} aria-hidden="true" /> {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="ilp-wg-bt-anim">
        <EmbedBuilder key="embed" />
      </div>
    </div>
  );
};

/* ==========================================================================
   Expanded "Guest Account" panel — timed walkthrough
   ========================================================================== */

const GUEST_DURATION = 6000;

const GUEST_CARDS = [
  {
    Icon: CalendarDays,
    eyebrow: 'Manage their rental',
    title: 'One account for the whole trip.',
    copy: 'Everything renters need to run their trip lives in one convenient account.',
    features: ['Swap a vehicle', 'Modify trip details', 'Extend a rental', 'Pay a balance owed'],
    image: `${IMG}/guest-account/manage-rental.png`,
    imageAlt: 'A smiling woman using her phone to manage a car rental next to an SUV',
  },
  {
    Icon: MessagesSquare,
    eyebrow: 'Stay connected',
    title: 'Keep everyone in sync.',
    copy: 'Built-in messaging keeps renters and your team in sync before, during, and after the trip.',
    features: ['Send and receive in-app messages', 'Get real-time trip updates', 'Contact your staff anytime'],
    image: `${IMG}/guest-account/stay-connected.png`,
    imageAlt: "A man texting on his phone from the driver's seat of a car",
  },
  {
    Icon: FileText,
    eyebrow: 'Access important documents',
    title: "Paperwork, right when it's needed.",
    copy: 'Essential trip paperwork is available the moment a renter needs it.',
    features: ['Access the rental agreement', 'Access the insurance card', 'View booking details'],
    image: `${IMG}/guest-account/documents.png`,
    imageAlt: 'Hands holding a phone showing a digital rental agreement and insurance card',
  },
  {
    Icon: LifeBuoy,
    eyebrow: 'Get help on the road',
    title: 'Help on the road, built in.',
    copy: 'Direct access to support makes reporting an incident fast and painless.',
    features: ['Request roadside assistance', 'Report an incident in minutes', 'Submit FNOL information easily'],
    image: `${IMG}/guest-account/roadside.png`,
    imageAlt: 'A couple beside an SUV with a flat tire on a mountain road as a tow truck approaches',
  },
  {
    Icon: RefreshCw,
    eyebrow: 'Keep renting with your business',
    title: 'Turn one trip into many.',
    copy: 'Turn one trip into an ongoing relationship from a single connected experience.',
    features: ['Rebook available vehicles', 'Return to your rental website', 'Continue as a returning guest'],
    image: `${IMG}/guest-account/keep-renting.png`,
    imageAlt: 'A happy customer receiving car keys from a rental agent outside a dealership',
  },
];

/** Expanded "Guest Account" panel: playlist on the left, timed card on the right. */
const GuestAccountContent = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);
  const [entered, setEntered] = useState(true);

  const active = GUEST_CARDS[activeIndex];
  const bulletCount = active.features.length;
  // Reveal bullets across the first 70% of the card's time on screen.
  const visibleBullets = isPlaying
    ? Math.max(1, Math.ceil(Math.min(progress / 0.7, 1) * bulletCount))
    : bulletCount;

  // Drive the timeline with requestAnimationFrame so pausing freezes cleanly.
  useEffect(() => {
    if (!isPlaying) return undefined;
    let raf = 0;
    let start = null;
    setProgress(0);
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / GUEST_DURATION, 1);
      setProgress(p);
      if (p >= 1) {
        setActiveIndex((prev) => (prev + 1) % GUEST_CARDS.length);
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying, activeIndex]);

  // Entrance animation for the right-hand card whenever the active card changes.
  useEffect(() => {
    setEntered(false);
    const t = setTimeout(() => setEntered(true), 20);
    return () => clearTimeout(t);
  }, [activeIndex]);

  const selectCard = (i) => {
    if (i === activeIndex) {
      setIsPlaying((p) => !p);
      return;
    }
    setActiveIndex(i);
    setProgress(0);
  };

  return (
    <div className="ilp-wg-ga">
      {/* Left — heading, subcopy, and the title playlist */}
      <div>
        <p className="ilp-wg-bt-eyebrow">Guest Account Included</p>
        <h2 className="ilp-wg-bt-title">Their own account for booking management</h2>
        <p className="ilp-wg-bt-sub">
          Give renters one convenient place to manage their trip, access documents, message your team, make
          payments, request assistance, and book with you again.
        </p>

        <ul className="ilp-wg-ga-list">
          {GUEST_CARDS.map((card, i) => {
            const isActive = i === activeIndex;
            const CardIcon = card.Icon;
            return (
              <li key={card.title}>
                <div className="ilp-wg-ga-row">
                  {isActive ? (
                    <button
                      type="button"
                      onClick={() => setIsPlaying((p) => !p)}
                      aria-label={isPlaying ? 'Pause walkthrough' : 'Play walkthrough'}
                      className="ilp-wg-ga-dot ilp-wg-ga-dot--active"
                    >
                      {isPlaying ? (
                        <Pause size={14} className="ilp-wg-ga-glyph" aria-hidden="true" />
                      ) : (
                        <Play size={14} className="ilp-wg-ga-glyph" aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => selectCard(i)}
                      aria-label={`Show ${card.eyebrow}`}
                      className="ilp-wg-ga-dot"
                    >
                      <CardIcon size={16} aria-hidden="true" />
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => selectCard(i)}
                    className={`ilp-wg-ga-title${isActive ? ' is-active' : ''}`}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {card.eyebrow}
                  </button>
                </div>

                {isActive && (
                  <div className="ilp-wg-ga-track">
                    <span
                      className="ilp-wg-ga-fill"
                      style={{ width: `${(isPlaying ? progress : 1) * 100}%` }}
                    />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Right — one lifestyle card at a time */}
      <div className="ilp-wg-ga-stage">
        <div className={`ilp-wg-ga-card${entered ? ' is-entered' : ''}`}>
          <div className="ilp-wg-ga-photo">
            <img key={active.image} src={active.image} alt={active.imageAlt} className="ilp-wg-fill" />
          </div>

          <div className="ilp-wg-ga-body">
            <h3 className="ilp-wg-ga-card-title">{active.title}</h3>
            <p className="ilp-wg-ga-card-copy">{active.copy}</p>

            <ul className="ilp-wg-ga-features">
              {active.features.map((feature, i) => (
                <li key={feature} className={i < visibleBullets ? 'is-shown' : ''}>
                  <span className="ilp-wg-ga-check">
                    <Check size={12} aria-hidden="true" />
                  </span>
                  <span className="ilp-wg-ga-feature">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ==========================================================================
   Section — two click-to-expand panels
   ========================================================================== */

const PANELS = [
  { id: 'website', eyebrow: 'Rental Website' },
  { id: 'guest', eyebrow: 'Guest Account' },
];

const TEASERS = {
  website: {
    heading: 'Free rental website included with sign up',
    copy: 'Launch a custom branded, white labeled, rental website in minutes — or drop a booking link straight into the site you already have.',
    Mockup: RentalWebsiteMockup,
  },
  guest: {
    heading: 'Dedicated guest accounts for managing every booking',
    copy: 'Give renters one convenient place to manage their trip, access documents, send messages, make payments, request assistance, and book vehicles.',
    Mockup: GuestAccountMockup,
  },
};

/**
 * Two side-by-side teasers that expand in place to a full walkthrough.
 * Clicking (or Enter/Space) expands a panel; the other collapses away and
 * Escape closes.
 */
const WebsiteGuestSplit = ({
  showTitle = true,
  title = 'Put your brand at the center of every guest experience.',
}) => {
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!expandedId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setExpandedId(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expandedId]);

  return (
    <section aria-label="Included with every plan" className="ilp-wg">
      <div className={`ilp-wg-inner${expandedId ? ' is-expanded' : ''}`}>
        <div className="ilp-wg-head">
          <p className="ilp-wg-eyebrow">Your Brand, Our Technology</p>
          {showTitle && <h2 className="ilp-wg-title">{title}</h2>}

          {!expandedId && <GuestExperienceRow />}
        </div>

        <div className={`ilp-wg-panels${expandedId ? ' is-expanded' : ''}`}>
          {PANELS.map((panel, index) => {
            const isExpanded = expandedId === panel.id;
            const isHidden = expandedId !== null && !isExpanded;
            const teaser = TEASERS[panel.id];
            const wrapperClass = [
              'ilp-wg-slot',
              isExpanded ? 'is-open' : '',
              isHidden ? `is-hidden ${index === 0 ? 'is-hidden--left' : 'is-hidden--right'}` : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <div key={panel.id} className={wrapperClass}>
                {isExpanded ? (
                  /* Expanded: full content + animation */
                  <div className="ilp-wg-open">
                    <button
                      type="button"
                      onClick={() => setExpandedId(null)}
                      aria-label="Close"
                      className="ilp-wg-close"
                    >
                      <X size={20} aria-hidden="true" />
                    </button>

                    {panel.id === 'website' ? <BrandTechnologyContent /> : <GuestAccountContent />}
                  </div>
                ) : (
                  /* Collapsed: clickable teaser */
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(panel.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setExpandedId(panel.id);
                      }
                    }}
                    aria-label={`Expand ${panel.eyebrow}`}
                    className="ilp-wg-teaser"
                  >
                    {/* Corner-splay maximize glyph, top-right */}
                    <span aria-hidden="true" className="ilp-wg-max">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline className="ilp-wg-max-tr" points="14 4 20 4 20 10" />
                        <polyline className="ilp-wg-max-bl" points="10 20 4 20 4 14" />
                      </svg>
                    </span>

                    <div className="ilp-wg-teaser-eyebrow">
                      <p>{panel.eyebrow}</p>
                    </div>

                    <h3 className="ilp-wg-teaser-title">{teaser.heading}</h3>
                    <p className="ilp-wg-teaser-copy">{teaser.copy}</p>

                    <div className="ilp-wg-teaser-mock">
                      <teaser.Mockup />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WebsiteGuestSplit;
